import { reactive, ref } from 'vue'
import { ElTable, ElTableColumn } from '@park-ui/components/table'
import { ElPagination } from '@park-ui/components/pagination'
import { ElButton } from '@park-ui/components/button'
import { ElPopover } from '@park-ui/components/popover'
import { ElCheckbox, ElCheckboxGroup } from '@park-ui/components/checkbox'
import ElColumnPopover from '@park-ui/components/cig-table/src/columnPopover.vue'
import ElColumnTooitlp from '@park-ui/components/cig-table/src/columnTooitlp.vue'
import { ElTooltip } from '@park-ui/components/tooltip'
import { ElIcon } from '@park-ui/components/icon'
import { IconNotData } from '@park-ui/components/cig-icon'
import type { ComponentInternalInstance, PropType } from 'vue'
import type { TableProps } from '@park-ui/components/table/src/table/defaults'
import type { ColumnTypes } from '@park-ui/components/cig-table/src/table-column/defaults'
import type { PaginationProps } from '@park-ui/components/pagination/src/pagination'

export interface TablePropTypes<T> extends TableProps<T> {
    onSelectionChange(selection: any): void
}
export type PaginationPropTypes = Partial<PaginationProps>
export type TablePropType<T> = Partial<TablePropTypes<T>>
export type ActionType = {
    showAction: boolean
    notAdd: boolean
    notView: boolean
    notEdit: boolean
    notDel: boolean
    notAllDel: boolean
    notAction: boolean
    attribute: object
}

// interface CIGTablePropType {
//   columns: ColumnTypes[]
//   tableOption: TablePropType<any>
//   pageOption: PaginationPropTypes
//   showPage?: boolean
//   idKey?: string
//   defaultSelection: Array<any>
//   pageStyle: object
//   showSelection: boolean
//   showIndex: boolean
//   actionOption: Partial<ActionType>
//   selectable: () => boolean
//   reserveSelection: boolean
//   showColumn: boolean
// }

export default {
    columns: {
        type: Array as PropType<ColumnTypes[]>,
        default: () => [],
    },
    tableOption: {
        type: Object as PropType<TablePropType<any>>,
        default: {},
    },
    pageOption: {
        type: Object as PropType<PaginationPropTypes>,
        default: () => ({}),
    },
    showPage: {
        type: Boolean,
        default: true,
    },
    idKey: {
        type: String,
        default: 'id',
    },
    defaultSelection: {
        type: Array,
        default: [],
    },
    showSelection: {
        type: Boolean,
        default: true,
    },
    showIndex: {
        type: Boolean,
        default: true,
    },
    actionOption: {
        type: Object,
    },
    selectable: {
        type: Function as PropType<(row: any, index: number) => boolean>,
    },
    showColumn: {
        type: Boolean,
    },
    reserveSelection: {
        type: Boolean,
    },
    mode: {
        type: String,
        default: ''
    },
    placeholder: {
        type: String,
        default: '--'
    }
}

type RowType = {
    row: object
    column: object
    $index: number
}

export const tableEmits = {
    'selection-change': (selection: Array<any>, selectionList: Array<any>) =>
        null,
    select: (selection: Array<any>, row: any) => null,
    'select-all': (selection: Array<any>) => null,
    'current-page': (currentPage: number) => currentPage,
    'size-change': (sizes: number) => sizes,
    view: (row: RowType) => row,
    edit: (row: RowType) => row,
    del: (row: RowType) => row,
    add: (evt: MouseEvent) => evt,
    allDel: (selectionList: any) => selectionList,
    'update:columns': (columns: any) => columns,
    'un-checked': () => null,
}

export type TableEmits = typeof tableEmits

export const components = {
    ElTableColumn,
    ElTable,
    ElPagination,
    ElButton,
    ElPopover,
    ElCheckboxGroup,
    ElCheckbox,
    ElIcon,
    ElColumnPopover,
    ElTooltip,
    ElColumnTooitlp,
    IconNotData,
}

export const ActionEmits = (instance: ComponentInternalInstance) => {
    // 默认操作回调
    const view = (row: RowType) => {
        instance.emit('view', row)
    }
    const edit = (row: RowType) => {
        instance.emit('edit', row)
    }
    const del = (row: RowType) => {
        instance.emit('del', row)
    }
    const add = (evt: MouseEvent) => {
        instance.emit('add', evt)
    }
    const allDel = (selectionList: Array<any>) => {
        instance.emit('allDel', selectionList)
    }
    return {
        view,
        edit,
        del,
        add,
        allDel,
    }
}

// 支持显示多级数据
export const getRealLabel = (row: any, field: any, placeholder: string) => {
    const filedName = field
    let label = row[filedName]
    // if (filedName && !filedName.includes('.')) return label ? label : '-';
    if (typeof filedName === 'string') {
        if (filedName.indexOf('.')) {
            const attrArr = filedName.split('.')
            label = attrArr.reduce((prev, cur) => {
                if (prev[cur]) {
                    return prev[cur]
                } else {
                    return placeholder // 默认空字符串
                }
            }, row)
        }
    }
    return label ? label : placeholder;
}

export const initSelection = (instance: ComponentInternalInstance) => {
    const props = reactive<any>(instance.props)

    const selectionList = reactive<any>([])
    const pageRefName = ref<any>(null)
    const tableRefName = ref()
    // 改变选中项 存在多选无分页情况
    const onSelectionChange = (selection: any) => {
        selection.forEach((item: any) => {
            const node = findSelection(selectionList, item)
            if (!node) {
                selectionList.push(item)
            }
        })
        instance.emit('selection-change', selection, selectionList)
    }
    // 单个选中
    const onSelect = (selection: Array<any>, row: any) => {
        const { tableOption }: any = instance.props
        const node = findSelection(selection, row)
        if (node) {
            selectionList.push(node)
        } else {
            const nodeIndex = findSelectIndex(selectionList, row)
            selectionList.splice(nodeIndex, 1)
        }
        // instance.emit('select', selection, row)
        if (tableOption.onSelect) tableOption.onSelect(selection, row)
    }

    const onSelectAll = (selection: Array<any>) => {
        const { tableOption }: any = instance.props
        // console.log('select-all', selection)
        if (selection.length > 0) {
            selection.forEach((item: any) => {
                const nodeIndex = findSelectIndex(selectionList, item)
                if (nodeIndex === -1) selectionList.push(item)
            })
        } else {
            // 删除
            const deleteList = tableOption.data
            deleteList.forEach((item: any) => {
                const nodeIndex = findSelectIndex(selectionList, item)
                if (nodeIndex > -1) selectionList.splice(nodeIndex, 1)
            })
        }
        if (tableOption.onSelectAll) tableOption.onSelectAll(selection)
    }

    // 查询对象
    const findSelection = (arr: any[], row: any) => {
        return arr.find((x: any) => row[props.idKey] === x[props.idKey])
    }

    // 查询对象索引
    const findSelectIndex = (arr: any[], row: any) => {
        return arr.findIndex((x: any) => row[props.idKey] === x[props.idKey])
    }
    // 清空当前页选中
    const clearSelection = () => {
        tableRefName.value.clearSelection()
    }

    // 清空所有/部分选中
    const clearAllSelection = (data?: any) => {
        if (data && data.length > 0) {
            // 并不是所有选中项都被删除
            for (const i in data) {
                const index = findSelectIndex(selectionList, data[i])
                if (index >= 0) selectionList.splice(index, 1)
            }
        } else {
            clearSelection()
            selectionList.splice(0)
        }
    }

    return {
        onSelect,
        onSelectAll,
        onSelectionChange,
        findSelection,
        clearSelection,
        clearAllSelection,
        selectionList,
        tableRefName,
        pageRefName,
    }
}
